import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WhatsappLogo, Info, PaperPlaneTilt, Trash, CheckCircle } from "@phosphor-icons/react";

/** Admin-side CallMeBot config for another team member.
 *  Useful when a technician can't/won't do the pairing flow themselves —
 *  the gérant pastes the personal key here on their behalf.
 */
export default function AdminCallMeBotDialog({ open, onOpenChange, user, onSaved }) {
  const [phone, setPhone] = useState("");
  const [apikey, setApikey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(user?.callmebot_phone || user?.phone || "");
    setApikey(""); // never prefill — treated like a secret
  }, [open, user]);

  const configured = !!user?.callmebot_apikey;

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const payload = { phone: phone.trim() || null };
      if (apikey.trim()) payload.apikey = apikey.trim();
      await api.patch(`/users/${user.id}/callmebot`, payload);
      toast.success("CallMeBot enregistré pour " + user.name);
      setApikey("");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const testIt = async () => {
    if (!user) return;
    setTesting(true);
    try {
      await api.post(`/users/${user.id}/callmebot/test`);
      toast.success(`WhatsApp de test envoyé à ${user.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  const unlink = async () => {
    if (!user) return;
    if (!window.confirm(`Retirer la config CallMeBot de ${user.name} ? Il/elle ne recevra plus les alertes internes WhatsApp.`)) return;
    try {
      await api.patch(`/users/${user.id}/callmebot`, { phone: "", apikey: "" });
      onSaved && onSaved();
      toast.success("CallMeBot délié");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl"
        data-testid="admin-callmebot-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <WhatsappLogo size={18} weight="duotone" className="text-emerald-400" />
            CallMeBot — {user?.name}
            {configured && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">Actif</span>}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            Configure la clé WhatsApp personnelle de {user?.name} pour qu&apos;il/elle reçoive les alertes SAV critiques et notifs urgentes.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-[#005CFF]/10 border border-[#005CFF]/25 p-3 text-[11px] text-white/70 space-y-1">
          <div className="flex items-center gap-1.5 text-white font-semibold text-xs">
            <Info size={13} weight="duotone" /> Étapes à faire depuis LE téléphone de {user?.name || "l'équipier"}
          </div>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Ajouter <b>+34 644 51 95 23</b> aux contacts WhatsApp (nom : CallMeBot)</li>
            <li>Lui envoyer <span className="font-mono bg-white/10 px-1 rounded">I allow callmebot to send me messages</span></li>
            <li>CallMeBot répond avec la clé API perso (ex. <span className="font-mono">1234567</span>)</li>
            <li>Colle la clé ci-dessous et enregistre — c&apos;est prêt.</li>
          </ol>
        </div>

        <div>
          <Label className="text-white/60 text-[10px] uppercase tracking-widest">Numéro WhatsApp (E.164)</Label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+33612345678"
            className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
            data-testid="admin-callmebot-phone"
          />
        </div>
        <div>
          <Label className="text-white/60 text-[10px] uppercase tracking-widest">Clé API CallMeBot</Label>
          <Input
            type="password"
            value={apikey}
            onChange={(e) => setApikey(e.target.value)}
            placeholder={configured ? "Laisser vide pour conserver la clé actuelle" : "1234567"}
            className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
            data-testid="admin-callmebot-apikey"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-11 rounded-xl" data-testid="admin-callmebot-save">
            {saving ? "…" : (<><CheckCircle size={14} weight="bold" /> Enregistrer</>)}
          </Button>
          <Button
            onClick={testIt}
            disabled={testing || !configured}
            variant="ghost"
            className="border border-white/10 hover:bg-white/5 h-11 rounded-xl"
            data-testid="admin-callmebot-test"
          >
            <PaperPlaneTilt size={14} /> {testing ? "…" : "Envoyer test"}
          </Button>
        </div>

        {configured && (
          <button
            type="button"
            onClick={unlink}
            className="w-full text-[11px] text-white/40 hover:text-red-300 py-1 flex items-center justify-center gap-1"
            data-testid="admin-callmebot-unlink"
          >
            <Trash size={11} /> Retirer la config CallMeBot
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
